import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Alert } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import axiosInstance from '../../services/axiosConfig';
import { formatDateRange } from '../../utils/dateUtils';
import { eventHasImage, getEventImageSrc } from '../../utils/imageUtils';

const EventList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [highlightedEventId, setHighlightedEventId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [includePastEvents, setIncludePastEvents] = useState(false);

  // Check if we're in "participants mode" from the URL query param
  const searchParams = new URLSearchParams(location.search);
  const showParticipants = searchParams.get('showParticipants') === 'true';

  useEffect(() => {
    fetchEvents();

    // If we're in participants mode, show a helpful message
    if (showParticipants && !successMessage) {
      setSuccessMessage('Seleziona un evento per visualizzare o gestire i suoi partecipanti');
    }

    // Check for success message from navigation state (e.g., when returning from edit)
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      setHighlightedEventId(location.state.updatedEventId);

      // Clear the location state to avoid showing the message again on refresh
      window.history.replaceState({}, document.title);

      // Auto-dismiss the success message after 5 seconds
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [location, includePastEvents]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found. Please login again.');
      }
      
      // Build URL with includePast parameter if enabled
      const url = includePastEvents ? '/api/events?includePast=true' : '/api/events';
      
      const response = await axiosInstance.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Events received from server:', response.data);
      // Make sure we're setting an array to the events state
      if (Array.isArray(response.data)) {
        setEvents(response.data);
      } else if (response.data && Array.isArray(response.data.events)) {
        // If the response has the events in an events property
        setEvents(response.data.events);
      } else {
        // If we can't find an array, set an empty array and log an error
        console.error('Expected array of events but received:', response.data);
        setEvents([]);
      }
      setLoading(false);
    } catch (err) {
      setError('Errore nel caricamento degli eventi. Riprova.');
      console.error('Error fetching events:', err);
      setLoading(false);
    }
  };
  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axiosInstance.delete(`/api/events/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Remove the deleted event from the state
      setEvents(events.filter(event => {
        return (event.id !== id) && (event._id !== id);
      }));
      setDeleteModalVisible(false);
      setSelectedEvent(null);
    } catch (err) {
      setError('Errore durante l\'eliminazione dell\'evento. Riprova.');
      console.error('Error deleting event:', err);
    }
  }; const handleEdit = (event) => {
    // Navigate to the edit page using the correct route format
    navigate(`/admin/events/${event._id || event.id}/edit`);
  };

  const confirmDelete = (event) => {
    setSelectedEvent(event);
    setDeleteModalVisible(true);
  };
  const formatEventDateRange = (event) => {
    return formatDateRange(
      event.eventDate || event.event_date,
      event.eventEndDate || event.event_end_date || event.eventDate || event.event_date
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Data non disponibile';
    try {
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      const date = new Date(dateString);
      return date.toLocaleDateString('it-IT', options);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Data non valida';
    }
  };
  // Debug logging
  useEffect(() => {
    console.log('Current events state:', events);
  }, [events]);

  // Filter and search events (tollera campi mancanti)
  const filteredEvents = events.filter(event => {
    if (!event) return false;

    // Apply category filter
    const categoryMatch = categoryFilter === 'all' || event.category === categoryFilter;

    // Apply search term filter (case insensitive, null-safe)
    const q = (searchTerm || '').toLowerCase();
    const searchMatch = !q ||
      (event.title || '').toLowerCase().includes(q) ||
      (event.description || '').toLowerCase().includes(q) ||
      (event.location || '').toLowerCase().includes(q);

    return categoryMatch && searchMatch;
  });
  return (
    <div className="container-fluid min-vh-100" style={{ background: 'linear-gradient(135deg, #006e39, #52b788)' }}>
      <div className="row p-2 p-md-4">
        <div className="col-12">
          <div className="glass-container p-3 p-md-4 rounded-4" style={{ backdropFilter: 'blur(10px)', backgroundColor: 'rgba(255, 255, 255, 0.9)' }}>            <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
            <h1 style={{ color: '#006e39' }} className="mb-0 fs-3 fs-md-1">Lista eventi</h1>
            <div>
              <button
                className="btn"
                style={{ backgroundColor: '#006e39', color: 'white' }}
                onClick={() => navigate('/admin/events/create')}
              >
                <i className="bi bi-plus-circle me-2"></i>
                crea nuovo evento
              </button>
            </div>            </div>

            {error && <div className="alert alert-danger">{error}</div>}
            {successMessage && (
              <div className="alert alert-success alert-dismissible fade show" role="alert">
                <i className="bi bi-check-circle-fill me-2"></i>
                {successMessage}
                <button type="button" className="btn-close" onClick={() => setSuccessMessage('')}></button>
              </div>
            )}            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body">
                <div className="row mb-3">
                  <div className="col-12 col-md-5 mb-3 mb-md-0">
                    <div className="input-group input-group-lg">
                      <span className="input-group-text" style={{ backgroundColor: '#f8f9fa', borderColor: '#006e39' }}>
                        <i className="bi bi-search" style={{ color: '#006e39' }}></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        style={{ borderColor: '#006e39' }}
                        placeholder="Cerca per titolo, descrizione o località..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      {searchTerm && (
                        <button
                          className="btn btn-outline-danger"
                          onClick={() => setSearchTerm('')}
                          title="Cancella ricerca"
                        >
                          <i className="bi bi-x-lg"></i>
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="col-12 col-sm-6 col-md-3 mb-3 mb-md-0">
                    <select
                      className="form-select form-select-lg"
                      style={{ borderColor: '#006e39' }}
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                    >
                      <option value="all">Tutte le categorie</option>
                      <option value="acquaviva">Acquaviva</option>
                      <option value="atletica leggera">Atletica Leggera</option>
                      <option value="calcio">Calcio</option>
                      <option value="ciclismo">Ciclismo</option>
                      <option value="danza">Danza</option>
                      <option value="discipline orientali">Discipline Orientali</option>
                      <option value="equestri e cinofile">Equestri e Cinofile</option>
                      <option value="ginnastiche">Ginnastiche</option>
                      <option value="giochi">Giochi</option>
                      <option value="montagna">Montagna</option>
                      <option value="motorismo">Motorismo</option>
                      <option value="neve">Neve</option>
                      <option value="nuoto">Nuoto</option>
                      <option value="pallacanestro">Pallacanestro</option>
                      <option value="pallavolo">Pallavolo</option>
                      <option value="pattinaggio">Pattinaggio</option>
                      <option value="subacquea">Subacquea</option>
                      <option value="tennis">Tennis</option>
                      <option value="vela">Vela</option>
                      <option value="multi-sport">Multi-Sport</option>
                      <option value="manifestazione">Manifestazione</option>
                      <option value="stage">Stage</option>
                    </select>
                  </div>
                  <div className="col-6 col-md-2 mb-3 mb-md-0">
                    <div className="form-check form-switch d-flex align-items-center h-100">
                      <input
                        className="form-check-input me-2"
                        type="checkbox"
                        id="includePastEvents"
                        checked={includePastEvents}
                        onChange={(e) => setIncludePastEvents(e.target.checked)}
                        style={{ cursor: 'pointer', transform: 'scale(1.3)' }}
                      />
                      <label 
                        className="form-check-label" 
                        htmlFor="includePastEvents"
                        style={{ cursor: 'pointer', color: '#006e39', fontWeight: '500' }}
                      >
                        <i className="bi bi-clock-history me-1"></i>
                        Eventi passati
                      </label>
                    </div>
                  </div>
                  <div className="col-6 col-md-2 mb-3 mb-md-0">
                    <button
                      className="btn w-100"
                      style={{ backgroundColor: '#dc3545', color: 'white', height: '100%' }}
                      onClick={() => {
                        setSearchTerm('');
                        setCategoryFilter('all');
                        setIncludePastEvents(false);
                      }}
                    >
                      <i className="bi bi-x-circle me-1"></i> Reset
                    </button>
                  </div>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <small className="text-muted">
                    Ricerca in tempo reale {includePastEvents && <span className="badge bg-warning text-dark ms-2">Include eventi passati</span>}
                  </small>
                  <div className="text-muted">
                    <i className="bi bi-funnel me-2"></i>
                    {filteredEvents.length} / {events.length} eventi
                  </div>
                </div>
              </div>
            </div>            {loading ? (
              <div className="text-center my-5">
                <div className="spinner-border" role="status" style={{ color: '#006e39' }}>
                  <span className="visually-hidden">Caricamento...</span>
                </div>
                <p className="mt-2" style={{ color: '#006e39' }}>Caricamento eventi...</p>
              </div>
            ) : filteredEvents.length === 0 ? (<div className="alert" style={{ backgroundColor: '#eef8f3', color: '#006e39', borderColor: '#006e39' }}>
              <i className="bi bi-info-circle me-2"></i>
              Nessun evento trovato. {searchTerm || categoryFilter !== 'all' ? 'Prova ad aggiustare i tuoi filtri.' : 'Crea un nuovo evento per iniziare.'}
            </div>
            ) : (
              <div className="row">                {filteredEvents.map(event => {
                  // Check if registrations are open and if event is past
                  const now = new Date();
                  const eventDate = new Date(event.eventDate || event.event_date);
                  const registrationDeadline = event.registrationDeadline ? new Date(event.registrationDeadline) : null;
                  const isPastEvent = eventDate < now;
                  const isRegistrationOpen = registrationDeadline ? now < registrationDeadline : now < eventDate;
                  const hasLateRegistration = event.allowLateRegistration === true;
                  
                  return (
                  <div key={event._id || event.id} className="col-12 col-sm-6 col-lg-4 mb-4">
                <div className={`card h-100 shadow-sm border-0 ${(event._id || event.id) === highlightedEventId ? 'border border-3 border-success shadow-lg' : ''} ${isPastEvent ? 'opacity-75' : ''}`}
                  style={{
                    transition: 'all 0.3s ease',
                    borderRadius: '12px',
                    ...((event._id || event.id) === highlightedEventId
                      ? { transform: 'scale(1.02)' }
                      : (isPastEvent ? { backgroundColor: '#f8f9fa' } : {}))
                  }}
                >{eventHasImage(event) && getEventImageSrc(event) && (
                    <div style={{ position: 'relative' }}>
                      <img
                        src={getEventImageSrc(event)}
                        className="card-img-top"
                        alt={event.title}
                        style={{ height: '180px', objectFit: 'cover', borderRadius: '12px 12px 0 0' }}
                      />
                      {/* Registration status badge on image */}
                      <div style={{ position: 'absolute', top: '10px', right: '10px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        {isPastEvent && (
                          <span className="badge bg-secondary" style={{ fontSize: '0.75rem', padding: '6px 10px' }}>
                            <i className="bi bi-clock-history me-1"></i>
                            Evento Passato
                          </span>
                        )}
                        <span className={`badge ${isRegistrationOpen ? 'bg-success' : hasLateRegistration ? 'bg-warning text-dark' : 'bg-danger'}`} 
                              style={{ fontSize: '0.85rem', padding: '8px 12px' }}>
                          <i className={`bi ${isRegistrationOpen ? 'bi-check-circle-fill' : hasLateRegistration ? 'bi-unlock-fill' : 'bi-x-circle-fill'} me-1`}></i>
                          {isRegistrationOpen ? 'Iscrizioni Aperte' : hasLateRegistration ? 'Iscrizioni Tardive' : 'Iscrizioni Chiuse'}
                        </span>
                      </div>
                    </div>
                  )}<div className="card-body">
                  {/* Badge iscrizioni se non c'è immagine */}
                  {!(eventHasImage(event) && getEventImageSrc(event)) && (
                    <div className="mb-2 d-flex flex-wrap gap-1">
                      {isPastEvent && (
                        <span className="badge bg-secondary" style={{ fontSize: '0.75rem', padding: '5px 8px' }}>
                          <i className="bi bi-clock-history me-1"></i>
                          Evento Passato
                        </span>
                      )}
                      <span className={`badge ${isRegistrationOpen ? 'bg-success' : hasLateRegistration ? 'bg-warning text-dark' : 'bg-danger'}`} 
                            style={{ fontSize: '0.85rem', padding: '6px 10px' }}>
                        <i className={`bi ${isRegistrationOpen ? 'bi-check-circle-fill' : hasLateRegistration ? 'bi-unlock-fill' : 'bi-x-circle-fill'} me-1`}></i>
                        {isRegistrationOpen ? 'Iscrizioni Aperte' : hasLateRegistration ? 'Iscrizioni Tardive' : 'Iscrizioni Chiuse'}
                      </span>
                    </div>
                  )}
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h5 className="card-title mb-0" style={{ color: '#006e39', fontWeight: '600' }}>
                      {event.title || 'Evento Senza Titolo'}
                      {(event._id || event.id) === highlightedEventId &&
                        <span className="badge bg-success ms-2" style={{ fontSize: '0.7rem' }}>Aggiornato</span>
                      }
                    </h5>
                    <span className={`badge bg-${getCategoryBadgeColor(event.category)}`} style={{ fontSize: '0.75rem' }}>
                      {getCategoryDisplayName(event.category)}
                    </span>
                  </div>
                  <div className="mb-2 d-flex flex-wrap gap-1">
                    {(() => {
                      const scopeLabels = { national: 'Nazionale', regional: 'Regionale', territorial: 'Territoriale' };
                      const scopeBg = { national: 'danger', regional: 'warning', territorial: 'secondary' };
                      const normalizedScope = String(event.scope || 'territorial').toLowerCase();
                      return (
                        <span className={`badge bg-${scopeBg[normalizedScope] || 'secondary'}`} style={{ fontSize: '0.75rem' }}>
                          {scopeLabels[normalizedScope] || 'Non specificato'}
                        </span>
                      );
                    })()}
                  </div>
                    <p className="card-text text-muted" style={{ fontSize: '0.95rem' }}>
                      {truncateText(event.description, 100)}
                    </p>
                    
                    <div className="mb-2">
                      <i className="bi bi-geo-alt-fill text-danger me-2"></i>
                      <strong>Località:</strong> {event.location || 'Non specificata'}
                    </div>
                    
                    <div className="mb-2">
                      <i className="bi bi-calendar-event text-primary me-2"></i>
                      <strong>Data:</strong> {formatEventDateRange(event)}
                    </div>
                    
                    {registrationDeadline && (
                      <div className="mb-2">
                        <i className="bi bi-calendar-x text-warning me-2"></i>
                        <strong>Chiusura iscrizioni:</strong> {formatDate(registrationDeadline)}
                      </div>
                    )}
                    
                    <div className="mb-2">
                      <i className="bi bi-clock text-info me-2"></i>
                      <strong>Orario:</strong> {
                        (event.startTime && event.endTime) || (event.start_time && event.end_time) ?
                          `${event.startTime || event.start_time} - ${event.endTime || event.end_time}` : 'Non specificato'}
                    </div>
                    
                    <div className="mb-2">
                      <i className="bi bi-people-fill text-success me-2"></i>
                      <strong>Posti:</strong> {event.maxParticipants || event.max_participants || 'Non impostato'}
                    </div>
                    
                    <div className="mb-2">
                      <i className="bi bi-currency-euro text-warning me-2"></i>
                      <strong>Prezzo:</strong> {
                        event.price === 0 || event.price === '0' ?
                          'Gratuito' :
                          (event.price ? `€${Number(event.price).toFixed(2)}` : 'Non impostato')
                      }
                    </div>
                    
                    <div className="mb-2">
                      <i className="bi bi-eye-fill text-secondary me-2"></i>
                      <strong>Visibilità:</strong> {
                        event.is_public !== undefined ?
                          (event.is_public ? 'Pubblico' : 'Privato') :
                          (event.isPublic !== undefined ?
                            (event.isPublic ? 'Pubblico' : 'Privato') :
                            'Privato')
                      }
                    </div>
                  </div>
                  
                  <div className="card-footer bg-light d-flex flex-wrap justify-content-between align-items-center gap-2" style={{ borderRadius: '0 0 12px 12px' }}>
                    <button
                      className="btn btn-sm"
                      style={{ backgroundColor: '#006e39', color: 'white', borderRadius: '8px' }}
                      onClick={() => navigate(`/admin/events/${event._id || event.id}/participants`)}
                    >
                      <i className="bi bi-people me-1"></i> Partecipanti
                    </button>
                    
                    <div>
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        style={{ borderRadius: '8px' }}
                        onClick={() => handleEdit(event)}
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        style={{ borderRadius: '8px' }}
                        onClick={() => confirmDelete(event)}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
                  );
                })}
              </div>
            )}            {/* Back button at the bottom */}
            <div className="mt-4">
              <button
                className="btn"
                style={{ backgroundColor: '#006e39', color: 'white', borderRadius: '8px' }}
                onClick={() => navigate('/admin/events')}
              >
                <i className="bi bi-arrow-left me-2"></i> Torna alla Gestione Eventi
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModalVisible && selectedEvent && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">              <div className="modal-header" style={{ backgroundColor: '#006e39', color: 'white' }}>
              <h5 className="modal-title">Conferma Eliminazione</h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={() => setDeleteModalVisible(false)}
              ></button>
            </div>
              <div className="modal-body">
                <p>Sei sicuro di voler eliminare l'evento "{selectedEvent.title}"?</p>
                <p className="text-danger"><strong>Questa azione non può essere annullata.</strong></p>
              </div>              <div className="modal-footer">
                <button
                  type="button"
                  className="btn"
                  style={{ backgroundColor: '#6c757d', color: 'white' }}
                  onClick={() => setDeleteModalVisible(false)}
                >
                  Annulla
                </button>                <button
                  type="button"
                  className="btn"
                  style={{ backgroundColor: '#dc3545', color: 'white' }}
                  onClick={() => handleDelete(selectedEvent._id || selectedEvent.id)}
                >
                  <i className="bi bi-trash me-1"></i> Elimina
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to truncate text
const truncateText = (text, maxLength) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// Helper function to determine badge color based on category
const getCategoryBadgeColor = (category) => {
  if (!category) return 'secondary';

  const categoryMap = {
    'acquaviva': 'info',
    'atletica leggera': 'warning',
    'calcio': 'success',
    'ciclismo': 'primary',
    'danza': 'danger',
    'discipline orientali': 'dark',
    'equestri e cinofile': 'secondary',
    'ginnastiche': 'success',
    'giochi': 'info',
    'montagna': 'success',
    'motorismo': 'danger',
    'neve': 'info',
    'nuoto': 'info',
    'pallacanestro': 'warning',
    'pallavolo': 'primary',
    'pattinaggio': 'danger',
    'subacquea': 'info',
    'tennis': 'success',
    'vela': 'primary',
    'multi-sport': 'success'
  };

  return categoryMap[category.toLowerCase()] || 'secondary';
};

// Helper function to get display name for category
const getCategoryDisplayName = (category) => {
  // Convert to title case
  if (!category) return 'Other';
  return category.split(' ').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

export default EventList;
